<template>
  <div class="step-2-mapping">
    <div class="mapping-sidebar">
      <div class="sidebar-header">
        <div class="sidebar-title">FILES - {{ uploadedFiles.length }}</div>
      </div>
      <div class="file-list">
        <div
          class="file-card"
          v-for="(file, index) in uploadedFiles"
          :key="file.id"
        >
          <button
            class="remove-file-btn"
            @click.stop="removeFile(index)"
            title="Remove file"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 1L1 9M1 1L9 9"
                stroke="#9CA3AF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div class="file-icon-box">
            <svg
              width="16"
              height="20"
              viewBox="0 0 16 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.33333 1.66667L14 6.33333V16.6667C14 17.1087 13.8244 17.5326 13.5118 17.8452C13.1993 18.1577 12.7754 18.3333 12.3333 18.3333H3.66667C3.22464 18.3333 2.80072 18.1577 2.48816 17.8452C2.17559 17.5326 2 17.1087 2 16.6667V3.33333C2 2.89131 2.17559 2.46738 2.48816 2.15482C2.80072 1.84226 3.22464 1.66667 3.66667 1.66667H9.33333ZM8.5 2.5H3.66667C3.44565 2.5 3.23369 2.5878 3.07741 2.74408C2.92113 2.90036 2.83333 3.11232 2.83333 3.33333V16.6667C2.83333 16.8877 2.92113 17.0996 3.07741 17.2559C3.23369 17.4122 3.44565 17.5 3.66667 17.5H12.3333C12.5543 17.5 12.7663 17.4122 12.9226 17.2559C13.0789 17.0996 13.1667 16.8877 13.1667 16.6667V7.5H8.5V2.5ZM9.33333 3.67833V6.66667H12.3217L9.33333 3.67833ZM5.33333 9.16667H10.6667V10H5.33333V9.16667ZM5.33333 11.6667H10.6667V12.5H5.33333V11.6667ZM5.33333 14.1667H8.66667V15H5.33333V14.1667Z"
                fill="#10B981"
              />
            </svg>
          </div>
          <div class="file-details">
            <div class="file-name" v-overflow-tooltip>{{ file.name }}</div>
            <div class="file-size">{{ file.size }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="mapping-content" v-if="activeFile">
      <div class="global-mapping-title">
        Global Configuration (Applies to all files)
      </div>

      <div class="bom-id-mapping">
        <div class="bom-id-header">
          <div class="bom-id-title">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM7 9C7 6.24 9.24 4 12 4C14.76 4 17 6.24 17 9C17 11.88 14.12 16.19 12 18.88C9.9 16.21 7 11.85 7 9Z"
                fill="#d97706"
              />
              <circle cx="12" cy="9" r="2.5" fill="#d97706" />
            </svg>
            BOM Identification
          </div>
          <div class="bom-id-badge">REQUIRED</div>
        </div>

        <div class="bom-id-fields">
          <div class="bom-id-row">
            <div class="bom-id-field-info">
              <div class="bom-id-name">ERP MATERIAL ID</div>
            </div>
            <div class="bom-id-arrow">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="#9ca3af"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>maps to</span>
            </div>
            <div class="bom-id-cell-select">
              <div class="bom-id-label">EXCEL CELL</div>
              <div class="cell-select-area">
                <div
                  class="cell-address-display"
                  :class="{
                    'has-value': getIdentificationCell('parent_item_code'),
                    'error-border':
                      showErrors && !getIdentificationCell('parent_item_code'),
                  }"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 4H10V10H4V4ZM14 4H20V10H14V4ZM4 14H10V20H4V14ZM17 14V17H14V19H17V22H19V19H22V17H19V14H17Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span v-if="getIdentificationCell('parent_item_code')">{{
                    getIdentificationCell("parent_item_code")
                  }}</span>
                  <span v-else class="placeholder-text">No cell selected</span>
                </div>
                <button
                  class="btn-select-cell"
                  @click="openCellSelector('parent_item_code')"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 3L21 3L21 9M21 3L14 10M10 14L3 21M3 21L3 15M3 21L9 21"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Select Cell
                </button>
              </div>
            </div>
          </div>

          <div class="bom-id-divider-invisible"></div>

          <div class="bom-id-row">
            <div class="bom-id-field-info">
              <div class="bom-id-name">ERP BOM ID</div>
            </div>
            <div class="bom-id-arrow">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="#9ca3af"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>maps to</span>
            </div>
            <div class="bom-id-cell-select">
              <div class="bom-id-label">EXCEL CELL</div>
              <div class="cell-select-area">
                <div
                  class="cell-address-display"
                  :class="{
                    'has-value': getIdentificationCell('erp_bom_id'),
                    'error-border':
                      showErrors && !getIdentificationCell('erp_bom_id'),
                  }"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 4H10V10H4V4ZM14 4H20V10H14V4ZM4 14H10V20H4V14ZM17 14V17H14V19H17V22H19V19H22V17H19V14H17Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span v-if="getIdentificationCell('erp_bom_id')">{{
                    getIdentificationCell("erp_bom_id")
                  }}</span>
                  <span v-else class="placeholder-text">No cell selected</span>
                </div>
                <button
                  class="btn-select-cell"
                  @click="openCellSelector('erp_bom_id')"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 3L21 3L21 9M21 3L14 10M10 14L3 21M3 21L3 15M3 21L9 21"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Select Cell
                </button>
              </div>
            </div>
          </div>

          <div class="bom-id-divider"></div>

          <div class="bom-name-toggle-section">
            <div class="bom-name-texts">
              <div class="bn-title">BOM Name</div>
              <div class="bn-desc">How should each imported BOM be named?</div>
            </div>
            <div class="toggle-row" style="margin-bottom: 0">
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  :checked="getUseSameBomName()"
                  @change="toggleUseSameBomName($event.target.checked)"
                />
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Same as material name</span>
            </div>
          </div>

          <div
            v-if="getUseSameBomName()"
            class="bom-name-info-box"
            style="margin-top: 12px"
          >
            <AvailableIcon
              class="bom-name-info-icon"
              width="14"
              height="14"
              color="#7c3aed"
            />
            <span>
              Will use the <strong>Material Name</strong> associated with the
              <strong>ERP Material ID</strong>
            </span>
          </div>

          <div v-else class="bom-id-row" style="margin-top: 16px">
            <div class="bom-id-field-info">
              <div class="bom-id-name">BOM Name</div>
            </div>
            <div class="bom-id-arrow">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="#9ca3af"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>maps to</span>
            </div>
            <div class="bom-id-cell-select">
              <div class="bom-id-label">EXCEL CELL</div>
              <div class="cell-select-area">
                <div
                  class="cell-address-display"
                  :class="{
                    'has-value': getIdentificationCell('bom_name'),
                    'error-border':
                      showErrors && !getIdentificationCell('bom_name'),
                  }"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 4H10V10H4V4ZM14 4H20V10H14V4ZM4 14H10V20H4V14ZM17 14V17H14V19H17V22H19V19H22V17H19V14H17Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span v-if="getIdentificationCell('bom_name')">{{
                    getIdentificationCell("bom_name")
                  }}</span>
                  <span v-else class="placeholder-text">No cell selected</span>
                </div>
                <button
                  class="btn-select-cell"
                  @click="openCellSelector('bom_name')"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 3L21 3L21 9M21 3L14 10M10 14L3 21M3 21L3 15M3 21L9 21"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Select Cell
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="field-mapping-group">
        <div class="group-header">
          <div class="title">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 9H20M4 15H20M10 3V21M16 3V21"
                stroke="#6b7280"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            BOM Items Mapping
            <span class="detected-cols-info"
              >(Detected Columns - {{ globalMapping.columnsFound }})</span
            >
          </div>
          <div class="stats">
            {{ mappedCount }} / {{ bomFields.length }} mapped
          </div>
        </div>

        <div class="data-header-config">
          <div class="dh-body">
            <div class="dh-desc-wrapper">
              <p class="dh-desc">
                Specify the row number where your table data begins.
              </p>
              <div
                class="info-tooltip"
                v-tooltip="
                  'Select the first row of your BOM data (not the headers). We\u2019ll look above it and automatically find the nearest row containing column headers.'
                "
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <path
                    d="M12 16V12M12 8H12.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div class="dh-row">
              <div class="dh-field-info">
                <div class="dh-name">BOM Item Starts From</div>
              </div>
              <div class="dh-arrow">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke="#9ca3af"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="dh-cell-select">
                <div class="dh-label">EXCEL ROW</div>
                <div class="dh-select-area">
                  <input
                    class="dh-input"
                    type="text"
                    :value="localHeaderRow"
                    @input="sanitizeRowInput"
                    @blur="handleRowBlur"
                    @change="handleRowChange"
                  />
                  <button
                    class="btn-select-cell"
                    @click="openCellSelector('header_row')"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 3L21 3L21 9M21 3L14 10M10 14L3 21M3 21L3 15M3 21L9 21"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    Select Row
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="detected-columns-section"
          v-if="globalMapping.columnsFound > 0 || globalMapping.isParsed"
        >
          <template v-if="globalMapping.columnsFound > 0">
            <div class="section-header">
              DETECTED COLUMNS - {{ globalMapping.columnsFound }}
            </div>
            <div class="columns-tags">
              <span
                class="tag"
                v-for="(col, i) in globalMapping.detectedColumns"
                :key="i"
                >{{ col.name }}</span
              >
            </div>
          </template>
          <template v-else-if="globalMapping.isParsed">
            <div class="no-columns-msg">No Header Columns Found</div>
          </template>
        </div>

        <div class="bom-mapping-list">
          <div class="mapping-table">
            <div class="table-header">
              <div class="col-bom">BOM FIELD</div>
              <div class="col-flag">FLAG</div>
              <div class="col-excel">EXCEL COLUMN</div>
            </div>
            <div class="table-body">
              <div class="table-row" v-for="field in bomFields" :key="field.id">
                <div class="col-bom-label">
                  {{ field.label }}
                </div>
                <div class="col-flag">
                  <span v-if="field.required" class="req-badge">REQ</span>
                  <span v-else class="opt-badge">OPT</span>
                </div>
                <div class="col-excel">
                  <CustomDropdown
                    :class="{
                      'error-border-dd':
                        showErrors &&
                        field.required &&
                        !getMappedSelected(field.id).id,
                    }"
                    :list="dropdownList"
                    :selected="getMappedSelected(field.id)"
                    defaultLabel="Select Excel Column"
                    :enableSearch="true"
                    :defaultStyle="{
                      ddLabelFontSize: 'var(--text-s)',
                      labelHeight: '36px',
                    }"
                    @select="handleMapping(field.id, $event)"
                    @deselect="!field.required && handleMapping(field.id, null)"
                  >
                    <template #label="{ item, defaultLabel }">
                      <div class="custom-dropdown-label-wrapper" style="width: calc(100% - 30px);">
                        <div
                          class="elipsis-text mr-4"
                          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1;"
                          :title="item?.name ?? defaultLabel"
                        >
                          {{ item?.name ?? defaultLabel }}
                        </div>
                        <div
                          v-if="item?.id && !field.required"
                          class="clear-mapping-btn"
                          @click.stop="handleMapping(field.id, null)"
                          title="Clear selection"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18 6L6 18M6 6L18 18"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </template>
                  </CustomDropdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      :open="cellSelectorOpen"
      :title="cellSelectorTitle"
      :description="
        cellSelectorFieldId === 'header_row'
          ? 'Click on any row to select it as the starting row for BOM fields'
          : 'Click on the cell that contains the value for this field'
      "
      @close="closeCellSelector"
      :closeOnOutsideClick="true"
    >
      <div class="excel-modal-content">
        <ExcelViewer
          ref="excelViewer"
          v-if="activeFile"
          :file="activeFile.file"
          :selectable="true"
          :selectionMode="cellSelectorFieldId === 'header_row' ? 'row' : 'cell'"
          :showToolbar="true"
          :showSheetSelector="true"
          :showConfirmButton="false"
          height="450px"
          @cell-selected="onCellSelected"
          @row-selected="onRowSelected"
        />
      </div>
    </Modal>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import CustomDropdown from '@/components/common/customDropdown.vue'
import Modal from '@/components/common/modal.vue'
import ExcelViewer from '@/components/common/excelViewer.vue'
import AvailableIcon from '@/components/common/icons/availableIcon.vue'
import { alert } from '@/plugins/notification'

export default {
  name: 'Step2Mapping',
  components: {
    CustomDropdown,
    Modal,
    ExcelViewer,
    AvailableIcon
  },
  props: {
    uploadedFiles: {
      type: Array,
      required: true
    },
    globalMapping: {
      type: Object,
      required: true
    },
    bomFields: {
      type: Array,
      required: true
    },
    showErrors: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeIndex: 0,
      localHeaderRow: 1,
      cellSelectorOpen: false,
      cellSelectorFieldId: null
    }
  },
  watch: {
    activeFile: {
      immediate: true,
      async handler (newFile, oldFile) {
        if (newFile) {
          this.localHeaderRow = this.globalMapping.headerRow || 1

          const needsReparse =
            !this.globalMapping.isParsed ||
            this.globalMapping.parsedFileId !== newFile.id

          if (needsReparse) {
            const isFileSwitch = this.globalMapping.isParsed
            await this.onHeaderRowChange(this.localHeaderRow, isFileSwitch)
          }
        }
      }
    }
  },
  computed: {
    activeFile () {
      if (this.uploadedFiles && this.uploadedFiles.length > 0) {
        return this.uploadedFiles[this.activeIndex]
      }
      return null
    },
    dropdownList () {
      if (!this.globalMapping) return []
      return this.globalMapping.detectedColumns
    },
    mappedCount () {
      if (!this.globalMapping || !this.globalMapping.mappings) return 0
      return this.bomFields.filter((f) => this.globalMapping.mappings[f.id])
        .length
    },
    cellSelectorTitle () {
      if (this.cellSelectorFieldId === 'parent_item_code') {
        return 'Select Cell for ERP Material ID'
      }
      if (this.cellSelectorFieldId === 'erp_bom_id') {
        return 'Select Cell for ERP BOM ID'
      }
      if (this.cellSelectorFieldId === 'bom_name') {
        return 'Select Cell for BOM Name'
      }
      if (this.cellSelectorFieldId === 'header_row') {
        return 'Select Header Row'
      }
      return 'Select Cell'
    },
    completedFilesCount () {
      return this.globalMapping.isFullyMapped ? this.uploadedFiles.length : 0
    },
    pendingFilesCount () {
      return this.globalMapping.isFullyMapped ? 0 : this.uploadedFiles.length
    }
  },
  methods: {
    removeFile (index) {
      this.$emit('remove-file', index)
    },
    getMappedSelected (fieldId) {
      if (
        !this.globalMapping.mappings ||
        !this.globalMapping.mappings[fieldId]
      ) {
        return { id: null, name: 'Select Excel Column' }
      }
      return this.globalMapping.mappings[fieldId]
    },
    checkIsFullyMapped (mappingObj) {
      if (!mappingObj) return false

      for (const field of this.bomFields) {
        if (
          field.required &&
          (!mappingObj.mappings || !mappingObj.mappings[field.id])
        ) {
          return false
        }
      }

      if (
        !mappingObj.identificationCells ||
        !mappingObj.identificationCells.parent_item_code ||
        !mappingObj.identificationCells.erp_bom_id
      ) {
        return false
      }

      const useSameBomName =
        mappingObj.bomNameFromMaterial !== undefined
          ? mappingObj.bomNameFromMaterial
          : true
      if (
        !useSameBomName &&
        (!mappingObj.identificationCells ||
          !mappingObj.identificationCells.bom_name)
      ) {
        return false
      }

      return true
    },
    handleMapping (fieldId, selectedOption) {
      const updatedMapping = {
        ...this.globalMapping,
        mappings: { ...(this.globalMapping.mappings || {}) }
      }
      updatedMapping.mappings[fieldId] = selectedOption || null

      updatedMapping.isFullyMapped = this.checkIsFullyMapped(updatedMapping)

      this.$emit('update-mapping', updatedMapping)
    },

    async extractColumns (fileObj, dataRow) {
      try {
        const data = await this.readFile(fileObj.file)

        if (dataRow <= 1) {
          return []
        }

        const workbook = XLSX.read(data, {
          type: 'array',
          sheetRows: dataRow - 1
        })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]

        const json = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: ''
        })
        let headerRowData = null

        for (let i = json.length - 1; i >= 0; i--) {
          const row = json[i]
          const hasValues = row.some(
            (cell) =>
              cell !== null && cell !== undefined && String(cell).trim() !== ''
          )
          if (hasValues) {
            headerRowData = row
            break
          }
        }

        if (!headerRowData) {
          return []
        }

        const columns = []
        headerRowData.forEach((colName, index) => {
          if (
            colName !== null &&
            colName !== undefined &&
            String(colName).trim() !== ''
          ) {
            const name = String(colName).trim()
            columns.push({
              id: name,
              name: name,
              columnNumber: index + 1
            })
          }
        })

        return columns
      } catch (error) {
        console.error('Error extracting columns:', error)
        alert(
          'Unable to read this file. It might be corrupted or password protected.'
        )
        return []
      }
    },

    readFile (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e)
        reader.readAsArrayBuffer(file)
      })
    },

    async onHeaderRowChange (val, isFileSwitch = false) {
      const row = parseInt(val, 10)
      if (isNaN(row) || row < 1) return

      const cols = await this.extractColumns(this.activeFile, row)

      let newMappings = {}
      let newIdentificationCells = {
        ...this.globalMapping.identificationCells
      }

      if (isFileSwitch) {
        newIdentificationCells = {}

        const oldCols = this.globalMapping.detectedColumns || []
        const oldColsStr = JSON.stringify(oldCols)
        const newColsStr = JSON.stringify(cols)

        if (oldColsStr === newColsStr && oldCols.length > 0) {
          newMappings = { ...this.globalMapping.mappings }
        }
      }

      const updatedMapping = {
        ...this.globalMapping,
        headerRow: row,
        detectedColumns: cols,
        columnsFound: cols.length,
        parsedFileId: this.activeFile.id,
        mappings: newMappings,
        identificationCells: newIdentificationCells,
        isParsed: true
      }

      updatedMapping.isFullyMapped = this.checkIsFullyMapped(updatedMapping)

      this.$emit('update-mapping', updatedMapping)
    },

    sanitizeRowInput (e) {
      const val = e.target.value.replace(/\D/g, '')
      if (val !== e.target.value) {
        e.target.value = val
      }
      const newRow = val === '' ? '' : parseInt(val, 10)
      if (this.localHeaderRow !== newRow) {
        this.localHeaderRow = newRow
        this.handleRowChange()
      }
    },

    handleRowBlur () {
      if (this.localHeaderRow === '' || this.localHeaderRow < 1) {
        this.localHeaderRow = 1
        if (this.globalMapping.headerRow !== 1) {
          this.onHeaderRowChange(1)
        }
      }
    },

    handleRowChange () {
      if (this.localHeaderRow !== '' && this.localHeaderRow >= 1) {
        if (this.globalMapping.headerRow === this.localHeaderRow) return
        this.onHeaderRowChange(this.localHeaderRow)
      }
    },

    getIdentificationCell (fieldId) {
      if (!this.globalMapping || !this.globalMapping.identificationCells) {
        return null
      }
      const cellData = this.globalMapping.identificationCells[fieldId]
      return cellData ? cellData.address : null
    },

    getUseSameBomName () {
      if (
        this.globalMapping &&
        this.globalMapping.bomNameFromMaterial !== undefined
      ) {
        return this.globalMapping.bomNameFromMaterial
      }
      return true
    },

    toggleUseSameBomName (val) {
      const updatedMapping = {
        ...this.globalMapping,
        bomNameFromMaterial: val
      }

      updatedMapping.isFullyMapped = this.checkIsFullyMapped(updatedMapping)
      this.$emit('update-mapping', updatedMapping)
    },

    openCellSelector (fieldId) {
      this.cellSelectorFieldId = fieldId
      this.cellSelectorOpen = true
    },

    closeCellSelector () {
      this.cellSelectorOpen = false
      this.cellSelectorFieldId = null
      if (this.$refs.excelViewer) {
        this.$refs.excelViewer.clearSelection()
      }
    },

    async onCellSelected (cellInfo) {
      if (!this.cellSelectorFieldId) return

      if (this.cellSelectorFieldId === 'header_row') {
        return
      }

      const updatedMapping = {
        ...this.globalMapping,
        identificationCells: {
          ...(this.globalMapping.identificationCells || {})
        }
      }

      updatedMapping.identificationCells[this.cellSelectorFieldId] = {
        sheet: cellInfo.sheet,
        address: cellInfo.address,
        row: cellInfo.row,
        col: cellInfo.col,
        colLetter: cellInfo.colLetter,
        value: cellInfo.value
      }

      updatedMapping.isFullyMapped = this.checkIsFullyMapped(updatedMapping)

      this.$emit('update-mapping', updatedMapping)

      this.closeCellSelector()
    },

    onRowSelected (event) {
      if (this.cellSelectorFieldId === 'header_row') {
        const rowNum = event.row
        this.localHeaderRow = rowNum
        this.handleRowChange()
        this.closeCellSelector()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.step-2-mapping {
  display: flex;
  gap: 20px;
  width: 100%;
  min-height: 480px;
}

.mapping-sidebar {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  align-self: flex-start;
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.sidebar-title {
  font-size: var(--text-xs1);
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.stats-overview {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--text-xs1);
  font-weight: 600;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &.completed {
    color: #10b981;
    .dot {
      background-color: #10b981;
    }
  }

  &.pending {
    color: #f59e0b;
    .dot {
      background-color: #f59e0b;
    }
  }
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  gap: 12px;
  background-color: #ffffff;
  position: relative;
  min-width: 0;

  &:hover {
    .remove-file-btn {
      opacity: 1;
    }
  }
}

.remove-file-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: #f3f4f6;
    path {
      stroke: #ef4444;
    }
  }
}

.file-icon-box {
  width: 32px;
  height: 32px;
  background-color: #ecfdf5;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  min-width: 0;
  padding-right: 20px;
}

.file-name {
  font-size: var(--text-s);
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: var(--text-xs2);
  color: #9ca3af;
  margin-bottom: 4px;
}

.mapping-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;
}

.global-mapping-title {
  font-size: var(--text-m, 1rem);
  font-weight: 700;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 12px;
  margin-bottom: -4px;
}

.field-mapping-group {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background-color: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.data-header-config {
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.dh-body {
  padding: 16px;
}

.dh-desc-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.dh-desc {
  font-size: var(--text-xs1);
  color: #6b7280;
  margin: 0;
}

.info-tooltip {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  color: #9ca3af;
  transition: color 0.2s ease;

  &:hover {
    color: #4338ca;
  }
}

.dh-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-top: 12px;
}

.dh-field-info {
  flex: 0 0 140px;
  background-color: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 8px 12px;
}

.dh-name {
  font-size: var(--text-xs2);
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.dh-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #9ca3af;
  flex-shrink: 0;
  padding: 8px 0px;
}

.dh-cell-select {
  flex: 1;
}

.dh-select-area {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dh-label {
  font-size: var(--text-xs);
  font-weight: 700;
  color: #4338ca;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.dh-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #f9fafb;
  font-size: var(--text-s);
  color: #374151;
  font-weight: 600;
  font-family: inherit;
  min-height: 36px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #6366f1;
    background-color: #ffffff;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
  }
}

.detected-columns-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  background-color: #ffffff;
}

.no-columns-msg {
  font-size: var(--text-xs2);
  color: #ef4444;
  font-weight: 500;
  padding-top: 4px;
}

.section-header {
  font-size: var(--text-xs1);
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.columns-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .tag {
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    color: #4b5563;
    font-size: var(--text-xs);
    padding: 5px 10px;
    border-radius: 8px;
    font-weight: 500;
  }
}

.bom-id-mapping {
  border: 1px solid #fbbf24;
  border-radius: 10px;
  background-color: #ffffff;
  overflow: hidden;
  flex-shrink: 0;
}

.bom-id-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #fef3c7;
  background-color: #fffbeb;
}

.bom-id-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--text-s);
  font-weight: 700;
  color: #b45309;
}

.bom-id-badge {
  background-color: #fde68a;
  color: #92400e;
  font-size: var(--text-xs1);
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 12px;
}

.bom-id-fields {
  padding: 10px 12px;
}

.bom-id-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

.bom-id-field-info {
  flex: 0 0 140px;
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 12px;
}

.bom-id-label {
  font-size: var(--text-xs);
  font-weight: 700;
  color: #92400e;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
}

.bom-id-name {
  font-size: var(--text-xs2);
  color: #92400e;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bom-id-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #9ca3af;
  font-size: var(--text-xs);
  font-weight: 500;
  gap: 2px;
  flex-shrink: 0;
}

.bom-id-cell-select {
  flex: 1;
}

.cell-select-area {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cell-address-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #f9fafb;
  font-size: var(--text-s);
  color: #9ca3af;
  min-height: 36px;

  svg {
    color: #d1d5db;
    flex-shrink: 0;
  }

  &.has-value {
    border-color: #c7d2fe;
    background-color: #eef2ff;
    color: #4f46e5;
    font-weight: 600;

    svg {
      color: #6366f1;
    }
  }
}

.placeholder-text {
  font-weight: 400;
}

.btn-select-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  background-color: #eef2ff;
  color: #4f46e5;
  font-size: var(--text-xs2);
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e0e7ff;
    border-color: #a5b4fc;
  }

  &:active {
    background-color: #c7d2fe;
  }
}

.bom-id-divider {
  height: 1px;
  background-color: #fef3c7;
  margin: 12px 0;
}

.bom-name-toggle-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.bom-name-texts {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bn-title {
  font-size: var(--text-s);
  font-weight: 600;
  color: #374151;
}

.bn-desc {
  font-size: var(--text-xs1);
  color: #9ca3af;
}

.bom-mapping-list {
  display: flex;
  flex-direction: column;
  padding: 0 16px 16px 16px;
  background-color: #ffffff;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--text-s);
    font-weight: 700;
    color: #4b5563;

    .detected-cols-info {
      font-size: var(--text-xs2);
      font-weight: 500;
      color: #9ca3af;
      margin-left: 4px;
    }
  }

  .stats {
    font-size: var(--text-xs2);
    color: #9ca3af;
    font-weight: 500;
  }
}

.mapping-table {
  width: 100%;
}

.table-header {
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: var(--text-xs1);
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
}

.table-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
}

.col-bom {
  flex: 2;
  display: flex;
  align-items: center;
  gap: 6px;
}

.col-bom-label {
  flex: 2;
  font-size: var(--text-s);
  color: #374151;
}

.col-flag {
  flex: 1;
}

.req-badge {
  background-color: #fee2e2;
  color: #ef4444;
  font-size: var(--text-xs1);
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 12px;
}

.opt-badge {
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: var(--text-xs1);
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 12px;
}

.col-excel {
  flex: 2;
  min-width: 0;
}

.excel-modal-content {
  min-width: 700px;
  max-width: 900px;
}

.error-border {
  border-color: #ef4444 !important;
  background-color: #fef2f2 !important;
}

::v-deep .error-border-dd .select-label {
  border-color: #ef4444 !important;
  background-color: #fef2f2 !important;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.toggle-label {
  font-size: var(--text-xs2);
  color: #4b5563;
  font-weight: 500;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #8b5cf6;
}

input:checked + .slider:before {
  transform: translateX(16px);
}

.bom-name-info-box {
  background-color: #f5f3ff;
  border: 1px solid #ede9fe;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: var(--text-xs1);
  color: #7c3aed;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.bom-name-info-icon {
  flex-shrink: 0;
}

.bom-name-info-box strong {
  color: #6d28d9;
  font-weight: 700;
}

.bom-id-divider-dotted {
  height: 1px;
  border-top: 1px dashed #e5e7eb;
  margin: 12px 0;
}

.bom-id-divider-invisible {
  height: 1px;
  margin: 12px 0;
  visibility: hidden;
}

.custom-dropdown-label-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.clear-mapping-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  margin-left: 4px;
  transition: all 0.2s ease;
}

.clear-mapping-btn:hover {
  background-color: #f3f4f6;
  color: #ef4444;
}
</style>
